import React, { useEffect, useMemo, useState } from 'react';
import {
    Portal,
    IconButton,
    Tooltip,
    CircularProgress,
    Alert,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TableChartIcon from '@mui/icons-material/TableChart';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import ClientService from '../../../Services/client.service';
import { beautifyNumber } from '../../../Utils/FormatNumber';
import {
    downloadUploadsBreakdownExcel,
    downloadUploadsBreakdownPdf,
    formatMonthKey,
    formatUploadMonth,
    uploadsBreakdownHeaders,
} from '../../../Utils/uploadsBreakdownExport';
import modalStyles from '../../JournalEntryies/SubComponents/JournalEntryTemplatesModal.module.scss';

const TYPE_TILES = [
    { key: 'expenses', label: 'Expenses', color: '#304FFF' },
    { key: 'incomes', label: 'Incomes', color: '#2e7d32' },
    { key: 'paymentAcceptances', label: 'PaymentAcceptances', color: '#ed6c02' },
    { key: 'otherDocuments', label: 'OtherDocuments', color: '#6B7280' },
];
const COUNT_KEYS = ['expenses', 'incomes', 'paymentAcceptances', 'otherDocuments', 'billableUploads', 'processedPages'];
const BILLABLE_COL = 5;

const cellSx = { py: 0.6, px: 1, fontSize: 12, textAlign: 'center', whiteSpace: 'nowrap' };
const headSx = { ...cellSx, fontWeight: 700, color: '#fff', backgroundColor: '#304FFF' };

/**
 * A client's uploads per upload month and document type, on the same basis as upload billing,
 * with PDF / Excel downloads of the same table.
 */
const UploadsBreakdownModal = ({ open, onClose, caseId, clientName, token, direction = 'rtl' }) => {
    const { t } = useTranslation();
    const isRtl = direction === 'rtl';
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [exporting, setExporting] = useState(null);

    useEffect(() => {
        if (!open || !caseId || !token) return undefined;
        let cancelled = false;
        setLoading(true);
        setError(null);
        ClientService.GetClientUploadsBreakdown(token, caseId)
            .then((res) => { if (!cancelled) setData(res); })
            .catch(() => { if (!cancelled) setError(t('UploadsBreakdownFailed')); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [open, caseId, token, t]);

    const months = useMemo(() => [...(data?.months || [])].reverse(), [data]);
    const totals = data?.totals;
    const hasLegacyMonths = months.some((m) => m.fromUsageTable === false);
    const busy = exporting !== null;

    const handleExport = async (format) => {
        if (!data) return;
        setExporting(format);
        try {
            const args = { data, clientName, clientCase: caseId, token, t };
            if (format === 'pdf') await downloadUploadsBreakdownPdf(args);
            else downloadUploadsBreakdownExcel(args);
        } catch (e) {
            toast.error(e?.message || t('DownloadFailed'));
        } finally {
            setExporting(null);
        }
    };

    if (!open) return null;

    const count = (n) => beautifyNumber(Number(n) || 0, 0, true);

    const splitTooltip = (r) => (
        <div style={{ fontSize: 12, lineHeight: 1.6 }}>
            <div style={{ fontWeight: 700 }}>{`${t('ReportingMonth')} ${formatMonthKey(r.monthKey)}`}</div>
            {TYPE_TILES.filter((tile) => r[tile.key] > 0).map((tile) => (
                <div key={tile.key}>{`${t(tile.label)}: ${count(r[tile.key])}`}</div>
            ))}
        </div>
    );

    const splitChips = (m) => {
        const split = m.savedInReportingMonths || [];
        if (split.length === 0) return '—';
        return (
            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', justifyContent: 'center' }}>
                {split.map((r) => (
                    <Tooltip key={r.monthKey} title={splitTooltip(r)} arrow>
                        <Chip
                            size="small"
                            label={`${formatMonthKey(r.monthKey)} · ${count(r.total)}`}
                            sx={{
                                height: 22,
                                fontSize: 11,
                                fontWeight: 600,
                                direction: 'ltr',
                                backgroundColor: r.monthKey === m.monthKey ? '#E8F5E9' : '#FFF3E0',
                                color: r.monthKey === m.monthKey ? '#2e7d32' : '#b45309',
                            }}
                        />
                    </Tooltip>
                ))}
            </div>
        );
    };

    return (
        <Portal>
            <div
                className={`${modalStyles.overlay} ${isRtl ? modalStyles.rtl : modalStyles.ltr}`}
                onClick={busy ? undefined : onClose}
            >
                <div dir={direction} onClick={(e) => e.stopPropagation()}>
                    <div className={modalStyles.modal} style={{ width: 'min(1080px, calc(100vw - 32px))', minHeight: '50vh' }}>
                        <div className={modalStyles.body}>
                            <div className={modalStyles.header}>
                                <Tooltip title={t('Close')}>
                                    <span>
                                        <IconButton onClick={onClose} disabled={busy} className={modalStyles.closeIcon}>
                                            <CancelIcon className={modalStyles.closeIcon} />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                                <div>{t('UploadsBreakdownTitle')}</div>
                                <div />
                            </div>

                            <div className={modalStyles.scroll}>
                                {loading && (
                                    <div style={{ display: 'flex', justifyContent: 'center', padding: 32 }}>
                                        <CircularProgress size={28} />
                                    </div>
                                )}
                                {!loading && error && <Alert severity="error">{error}</Alert>}
                                {!loading && !error && data && months.length === 0 && (
                                    <p className={modalStyles.muted}>{t('NoData')}</p>
                                )}
                                {!loading && !error && months.length > 0 && (
                                    <>
                                        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
                                            {TYPE_TILES.map((tile) => (
                                                <div
                                                    key={tile.key}
                                                    style={{
                                                        flex: '1 1 140px',
                                                        border: `1px solid ${tile.color}33`,
                                                        borderTop: `3px solid ${tile.color}`,
                                                        borderRadius: 8,
                                                        padding: '8px 12px',
                                                        textAlign: 'center',
                                                    }}
                                                >
                                                    <div style={{ fontSize: 12, color: '#6B7280' }}>{t(tile.label)}</div>
                                                    <div style={{ fontSize: 18, fontWeight: 700, color: tile.color }}>{count(totals?.[tile.key])}</div>
                                                </div>
                                            ))}
                                            <div
                                                style={{
                                                    flex: '1 1 140px',
                                                    borderRadius: 8,
                                                    padding: '8px 12px',
                                                    textAlign: 'center',
                                                    background: '#EEF1FF',
                                                    border: '1px solid #304FFF55',
                                                }}
                                            >
                                                <div style={{ fontSize: 12, color: '#1E308F', fontWeight: 600 }}>{t('BillableUploads')}</div>
                                                <div style={{ fontSize: 18, fontWeight: 700, color: '#1E308F' }}>{count(totals?.billableUploads)}</div>
                                            </div>
                                        </div>

                                        <TableContainer sx={{ maxHeight: '46vh', border: '1px solid #E5E7EB', borderRadius: 1 }}>
                                            <Table size="small" stickyHeader>
                                                <TableHead>
                                                    <TableRow>
                                                        {[...uploadsBreakdownHeaders(t), t('SavedInReportingMonths')].map((h) => (
                                                            <TableCell key={h} sx={headSx}>{h}</TableCell>
                                                        ))}
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {months.map((m, idx) => (
                                                        <TableRow key={m.monthKey} sx={{ backgroundColor: idx % 2 === 1 ? '#F6F8FF' : '#fff' }}>
                                                            <TableCell sx={{ ...cellSx, fontWeight: 600 }}>{formatUploadMonth(m)}</TableCell>
                                                            {COUNT_KEYS.map((k, i) => (
                                                                <TableCell
                                                                    key={k}
                                                                    sx={i + 1 === BILLABLE_COL ? { ...cellSx, fontWeight: 700, color: '#1E308F' } : cellSx}
                                                                >
                                                                    {count(m[k])}
                                                                </TableCell>
                                                            ))}
                                                            <TableCell sx={{ ...cellSx, whiteSpace: 'normal', minWidth: 180 }}>{splitChips(m)}</TableCell>
                                                        </TableRow>
                                                    ))}
                                                    <TableRow sx={{ backgroundColor: '#D7DFFF' }}>
                                                        <TableCell sx={{ ...cellSx, fontWeight: 700 }}>{t('Total')}</TableCell>
                                                        {COUNT_KEYS.map((k) => (
                                                            <TableCell key={k} sx={{ ...cellSx, fontWeight: 700, color: '#1E308F' }}>{count(totals?.[k])}</TableCell>
                                                        ))}
                                                        <TableCell sx={cellSx} />
                                                    </TableRow>
                                                </TableBody>
                                            </Table>
                                        </TableContainer>

                                        <p className={modalStyles.muted} style={{ marginTop: 12, lineHeight: 1.6 }}>{t('UploadsBreakdownNote')}</p>
                                        <p className={modalStyles.muted} style={{ lineHeight: 1.6 }}>{t('SavedInReportingMonthsNote')}</p>
                                        {hasLegacyMonths && <p className={modalStyles.muted}>{t('UploadsBreakdownLegacyNote')}</p>}
                                    </>
                                )}
                            </div>

                            <div className={modalStyles.footer} style={{ justifyContent: 'flex-start' }}>
                                <button
                                    type="button"
                                    className={modalStyles.primaryBtn}
                                    disabled={busy || !data || months.length === 0}
                                    onClick={() => handleExport('pdf')}
                                >
                                    {exporting === 'pdf' ? <CircularProgress size={18} color="inherit" /> : <PictureAsPdfIcon fontSize="small" />}
                                    {t('DownloadPDFFile')}
                                </button>
                                <button
                                    type="button"
                                    className={modalStyles.ghostBtn}
                                    disabled={busy || !data || months.length === 0}
                                    onClick={() => handleExport('excel')}
                                >
                                    <TableChartIcon fontSize="small" />
                                    {t('DownloadExcelFile')}
                                </button>
                                <button type="button" className={modalStyles.secondaryBtn} onClick={onClose} disabled={busy}>
                                    {t('Close')}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Portal>
    );
};

export default UploadsBreakdownModal;
